import { atom, computed, map, type ReadableAtom } from 'nanostores';

import { clamp } from '@iconik-io/core/src/clamp';
import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';

import type { SyncClient } from '../../sync/client/sync_client.js';
import { syncAtom } from '../../sync/sync_atom.js';
import { syncMap } from '../../sync/sync_map.js';
import type {
  Annotation,
  MarkerLaneConfig,
  MarkerLaneConfigMap,
  MarkerLanes,
  Range,
  Segment,
} from '../../types.js';
import { createMarkerLanes } from '../../utils/marker_lane_utils.js';
import { createAnnotationsStore } from './annotations.js';
import type { createPlayableMediaStore } from './playable_media.js';
import type { createTimelineStore } from './timeline.js';

export type CurrentSegment = Omit<Partial<Segment>, 'annotation' | 'type'>;

const logger = DebugLogger.getInstance('unified_player_segment');

export function createSegmentStore<PlaylistItemMeta>(
  timelineStore: ReturnType<typeof createTimelineStore<PlaylistItemMeta>>,
  playableMedia: ReturnType<typeof createPlayableMediaStore>,
  syncClient: SyncClient,
) {
  const $segments = atom<Segment[]>([]);

  const $markerLaneConfigMap = map<MarkerLaneConfigMap>({});

  const $markerLanes: ReadableAtom<MarkerLanes> = computed(
    [$segments, $markerLaneConfigMap],
    (segments, laneOptions) => {
      const markerLanes: MarkerLanes = createMarkerLanes(segments, laneOptions);
      return markerLanes;
    },
  );

  const $dismissedSegment = map<CurrentSegment>();
  const $dismissedSegmentIds = atom<string[]>([]);

  const clearDismissedSegments = () => {
    $dismissedSegmentIds.set([]);
    $dismissedSegment.set({});
  };

  const setDismissedSegment = (segment: CurrentSegment) => {
    $dismissedSegmentIds.set([]);
    $dismissedSegment.set(segment);
  };

  const $currentSegment = syncMap<CurrentSegment>(
    'currentSegment',
    {},
    {
      syncClient,
    },
  );

  const $currentSegmentState = computed([$currentSegment], (segment) => {
    if (!segment.range || segment.range.start === segment.range.end) {
      return 'none';
    }
    if (segment.editing) {
      return 'editing';
    }

    return 'readonly';
  });

  const $selectedSegmentIds = syncAtom<string[]>('selectedSegmentIds', [], {
    syncClient,
  });

  const annotation = createAnnotationsStore(syncClient);

  // The saved drawing is fetched lazily, so it can arrive after edit mode opens.
  // Seed the draft the first time it is available for the segment being edited;
  // seeding again would resurrect a drawing the user has since cleared.
  let seededEditSegmentId: string | undefined;
  // $currentSegment is synced, so its editing flag can be another participant's edit
  let localEditSegmentId: string | undefined;

  function seedEditDraftAnnotation() {
    const currentSegment = $currentSegment.get();
    if (
      !currentSegment.editing ||
      !localEditSegmentId ||
      currentSegment.id !== localEditSegmentId ||
      seededEditSegmentId === currentSegment.id
    ) {
      return;
    }
    const segment = $segments
      .get()
      .find((segmentItem) => segmentItem.id === currentSegment.id);
    if (!segment?.annotation) {
      return;
    }
    seededEditSegmentId = currentSegment.id;
    annotation.setPersonalDraftAnnotation(segment.annotation);
  }

  function handleSegments(
    selectedSegmentIds: readonly string[],
    segments: readonly Segment[],
  ) {
    const segmentsMap = new Map<string, Segment>(
      segments.map((segment) => [segment.id, segment]),
    );
    const result: Annotation = {};
    for (const segmentId of selectedSegmentIds) {
      const segment = segmentsMap.get(segmentId);
      if (segment?.annotation) {
        Object.assign(result, segment.annotation);
      }
    }
    annotation.setContextualAnnotation(result);
    seedEditDraftAnnotation();

    const firstSegmentId = selectedSegmentIds[0];
    const firstSegment = segmentsMap.get(firstSegmentId);
    if (selectedSegmentIds.length === 0 && !$currentSegment.get().editing) {
      clearCurrentSegment();
      return;
    }
    if (!firstSegment) {
      return;
    }
    if (selectedSegmentIds.length === 1) {
      setCurrentSegment(firstSegment);
      return;
    }
    // When combining multiple segments to display the frame overview, we always want to treat them as a single-frame segment
    const combinedSegment = {
      ...firstSegment,
      id: selectedSegmentIds.join('_'),
      range:
        firstSegment.range !== undefined
          ? {
              start: firstSegment.range.start,
              end: firstSegment.range.start,
            }
          : undefined,
    };
    setCurrentSegment(combinedSegment);
  }

  $selectedSegmentIds.listen((selectedSegmentIds) => {
    handleSegments(selectedSegmentIds, $segments.get());
  });

  $segments.listen((segments) => {
    handleSegments($selectedSegmentIds.get(), segments);
  });

  const setSegments = (segments: Segment[]) => {
    logger.debug('setSegments, segments count', segments.length);
    $segments.set(segments);
  };

  const setMarkerLanes = (config: MarkerLaneConfigMap) => {
    $markerLaneConfigMap.set(config);
  };

  const addMarkerLane = (key: string, config: MarkerLaneConfig) => {
    $markerLaneConfigMap.setKey(key, config);
  };

  const removeMarkerLane = (key: string) => {
    $markerLaneConfigMap.setKey(key, undefined);
  };

  const setCurrentSegment = (segment: Partial<Segment>) => {
    const currentSegmentId = $currentSegment.get().id;
    if (currentSegmentId === segment.id) {
      return;
    }
    $currentSegment.set(segment);
  };

  const setCurrentSegmentRange = (range: Partial<Range>) => {
    const { range: currentRange } = $currentSegment.get();
    const currentSegmentState = $currentSegmentState.get();
    if (
      currentSegmentState === 'readonly' ||
      (currentSegmentState === 'none' && currentRange)
    ) {
      clearCurrentSegment();
    }

    const { start: playableStart, end: playableEnd } =
      playableMedia.$playableRange.get();
    const scrubbing = playableMedia.$scrubbing.get();

    const clampedStartTime = clamp(
      range.start ?? currentRange?.start ?? playableStart,
      playableStart,
      playableEnd,
    );
    const clampedEndTime = clamp(
      range.end ?? currentRange?.end ?? playableEnd,
      playableStart,
      playableEnd,
    );

    // When giving a single range value, adjust the other value to keep the start before the end
    const adjustedStartTime =
      !scrubbing && !range.start
        ? Math.min(clampedStartTime, clampedEndTime)
        : clampedStartTime;
    const adjustedEndTime =
      !scrubbing && !range.end
        ? Math.max(adjustedStartTime, clampedEndTime)
        : clampedEndTime;

    const newRange: Range = {
      start: playableMedia.getFrameAlignedTime(adjustedStartTime),
      end: playableMedia.getFrameAlignedTime(adjustedEndTime),
    };

    $currentSegment.setKey('range', newRange);
  };

  const setCurrentSegmentStart = (start: number) => {
    const currentEnd = $currentSegment.get().range?.end ?? 0;
    const playableEnd = playableMedia.$playableRange.get().end;
    if (start > currentEnd) {
      setCurrentSegmentRange({ start, end: playableEnd });
    } else {
      setCurrentSegmentRange({ start });
    }
  };

  const setCurrentSegmentEnd = (end: number) => {
    const currentStart = $currentSegment.get().range?.start ?? Infinity;
    const playableStart = playableMedia.$playableRange.get().start;
    if (end < currentStart) {
      setCurrentSegmentRange({ start: playableStart, end });
    } else {
      setCurrentSegmentRange({ end });
    }
  };

  const setCurrentSegmentStartToPlayhead = () => {
    const currentTime = playableMedia.$currentTime.get();
    setCurrentSegmentStart(currentTime ?? 0);
    setCurrentSegmentEditing(true);
  };

  const setCurrentSegmentEndToPlayhead = () => {
    const currentTime = playableMedia.$currentTime.get();
    setCurrentSegmentEnd(currentTime ?? 0);
    setCurrentSegmentEditing(true);
  };

  const setCurrentSegmentStartProgress = (progress: number) => {
    setCurrentSegmentStart(timelineStore.progressToTime(progress) ?? 0);
  };

  const setCurrentSegmentEndProgress = (progress: number) => {
    setCurrentSegmentEnd(timelineStore.progressToTime(progress) ?? 0);
  };

  const setCurrentSegmentEditing = (editing: boolean) => {
    localEditSegmentId = editing ? $currentSegment.get().id : undefined;
    $currentSegment.setKey('editing', editing);

    if (editing) {
      seedEditDraftAnnotation();
      return;
    }
    seededEditSegmentId = undefined;
  };

  const setCurrentSegmentReplying = (replying: boolean) => {
    $currentSegment.setKey('replying', replying);
  };

  const $currentSegmentDuration = computed([$currentSegment], (segment) => {
    if (!segment.range) {
      return 0;
    }
    return segment.range.end - segment.range.start;
  });

  const $currentSegmentStart = computed([$currentSegment], (segment) => {
    return segment.range?.start;
  });

  const $currentSegmentEnd = computed([$currentSegment], (segment) => {
    return segment.range?.end;
  });

  const $currentSegmentStartProgress = computed(
    [$currentSegment, timelineStore.$timelineDuration],
    (segment) => {
      if (!segment.range) {
        return 0;
      }
      return timelineStore.timeToProgress(segment.range.start) ?? 0;
    },
  );

  const $currentSegmentEndProgress = computed(
    [$currentSegment, timelineStore.$timelineDuration],
    (segment) => {
      if (!segment.range) {
        return 0;
      }
      return timelineStore.timeToProgress(segment.range.end) ?? 0;
    },
  );

  const clearCurrentSegment = () => {
    const oldSegment = $currentSegment.get();
    if (oldSegment.range) {
      if (oldSegment.range.start === oldSegment.range.end) {
        clearDismissedSegments();
      } else {
        $dismissedSegmentIds.set($selectedSegmentIds.get());
        $dismissedSegment.set(oldSegment);
      }
    }
    logger.debug('Clear current segment');
    setSelectedSegmentIds([]);
    $currentSegment.set({ editing: true });
  };

  const setSelectedSegmentIds = (segmentIds: string[]) => {
    logger.debug('setSelectedSegmentIds', segmentIds);
    const currentSelectedSegmentIds = $selectedSegmentIds.get();
    if (
      currentSelectedSegmentIds.length === segmentIds.length &&
      currentSelectedSegmentIds.every((id, index) => id === segmentIds[index])
    ) {
      return;
    }
    $selectedSegmentIds.set(segmentIds);
  };

  const $currentLane = computed(
    [$selectedSegmentIds, $markerLanes],
    (selectedSegmentIds, markerLanes) => {
      if (selectedSegmentIds.length === 0) {
        return markerLanes.primary;
      }

      const currentSegmentId = selectedSegmentIds[0];

      for (const lane of Object.values(markerLanes)) {
        if (!lane?.markers) {
          continue;
        }
        if (
          Object.values(lane.markers).some((marker) =>
            marker.segments.some((segment) => segment.id === currentSegmentId),
          )
        ) {
          return lane;
        }
      }

      return markerLanes.primary;
    },
  );

  const selectMarker = (selector: (times: string[]) => string | undefined) => {
    const currentLane = $currentLane.get();
    if (!currentLane?.markers) {
      return;
    }

    const markerTimes = Object.keys(currentLane.markers).sort(
      (a, b) => Number(a) - Number(b),
    );
    const selectedTime = selector(markerTimes);

    if (selectedTime) {
      const selectedSegment = currentLane.markers[selectedTime];
      if (selectedSegment) {
        const segmentIds = selectedSegment.segments.map(
          (segment) => segment.id,
        );
        logger.debug('Select markers', segmentIds);
        setSelectedSegmentIds(segmentIds);
      }
    }
  };

  const selectFirstMarker = () => selectMarker((times) => times[0]);
  const selectLastMarker = () =>
    selectMarker((times) => times[times.length - 1]);

  const findClosestMarkerTime = (
    markerTimes: string[],
    direction: 1 | -1,
  ): string | undefined => {
    const currentTime = playableMedia.$currentTime.get();

    if (currentTime === undefined) {
      return undefined;
    }

    if (currentTime < Number(markerTimes[0]) && direction === -1) {
      return markerTimes[markerTimes.length - 1];
    }
    const closestTime = markerTimes.find((time, index) => {
      const timeNum =
        direction === 1
          ? Number(time)
          : Number(
              markerTimes[index + 1] ?? markerTimes[markerTimes.length - 1],
            );
      return direction === 1 ? timeNum >= currentTime : timeNum > currentTime;
    });

    return (
      closestTime ??
      (direction === 1 ? markerTimes[0] : markerTimes[markerTimes.length - 1])
    );
  };

  const selectAdjacentMarker = (direction: 1 | -1) => {
    const currentLane = $currentLane.get();
    if (!currentLane?.markers) {
      return;
    }

    const markerTimes = Object.keys(currentLane.markers).sort(
      (a, b) => Number(a) - Number(b),
    );
    const selectedSegmentIds = $selectedSegmentIds.get();

    if (selectedSegmentIds.length === 0) {
      const closestTime = findClosestMarkerTime(markerTimes, direction);
      if (closestTime) {
        return selectMarker(() => closestTime);
      }
      return direction === 1 ? selectFirstMarker() : selectLastMarker();
    }

    const currentIndex = markerTimes.findIndex((time) =>
      currentLane.markers[time].segments.some(
        (segment) => segment.id === selectedSegmentIds[0],
      ),
    );

    if (currentIndex === -1) {
      return direction === 1 ? selectFirstMarker() : selectLastMarker();
    }

    const adjacentIndex =
      (currentIndex + direction + markerTimes.length) % markerTimes.length;
    selectMarker((times) => times[adjacentIndex]);
  };

  const selectNextMarker = () => selectAdjacentMarker(1);
  const selectPreviousMarker = () => selectAdjacentMarker(-1);

  const selectAdjacentSegmentInMarker = (direction: 1 | -1) => {
    const currentLane = $currentLane.get();
    const selectedSegmentIds = $selectedSegmentIds.get();

    if (!currentLane?.markers || selectedSegmentIds.length === 0) {
      return;
    }

    const currentSegmentId = selectedSegmentIds[0];
    const currentTime = Object.keys(currentLane.markers).find((time) =>
      currentLane.markers[time].segments.some(
        (segment) => segment.id === currentSegmentId,
      ),
    );

    if (!currentTime) {
      return;
    }

    const currentMarker = currentLane.markers[currentTime];
    const currentIndex = currentMarker.segments.findIndex(
      (segment) => segment.id === currentSegmentId,
    );

    if (currentIndex === -1) {
      return;
    }

    const adjacentIndex =
      (currentIndex + direction + currentMarker.segments.length) %
      currentMarker.segments.length;
    const adjacentSegment = currentMarker.segments[adjacentIndex];

    setSelectedSegmentIds([adjacentSegment.id]);
  };

  const selectNextSegmentInMarker = () => selectAdjacentSegmentInMarker(1);
  const selectPreviousSegmentInMarker = () => selectAdjacentSegmentInMarker(-1);

  return {
    $segments,
    setSegments,
    $markerLanes,
    $currentSegment,
    $currentSegmentState,
    $dismissedSegment,
    $dismissedSegmentIds,
    clearDismissedSegments,
    setDismissedSegment,
    $selectedSegmentIds,
    setMarkerLanes,
    addMarkerLane,
    removeMarkerLane,
    setCurrentSegment,
    setCurrentSegmentEditing,
    setCurrentSegmentReplying,
    setCurrentSegmentStart,
    setCurrentSegmentEnd,
    setCurrentSegmentRange,
    setCurrentSegmentStartProgress,
    setCurrentSegmentEndProgress,
    setCurrentSegmentStartToPlayhead,
    setCurrentSegmentEndToPlayhead,
    clearCurrentSegment,
    setSelectedSegmentIds,
    $currentSegmentStart,
    $currentSegmentEnd,
    $currentSegmentDuration,
    $currentSegmentStartProgress,
    $currentSegmentEndProgress,
    annotation,
    selectNextMarker,
    selectPreviousMarker,
    selectNextSegmentInMarker,
    selectPreviousSegmentInMarker,
  };
}
